import { useState, useEffect, useCallback } from "react";
import { Plus, Search, FileText, Edit2, Trash2, ArrowRight } from "lucide-react";
import api from "../../services/api";
import QuoteForm from "./QuoteForm";
import { useAuth } from "../../context/AuthContext";

var STATUS_CLS = { aberta: "bg-blue-100 text-blue-700", respondida: "bg-amber-100 text-amber-700", convertida: "bg-emerald-100 text-emerald-700", cancelada: "bg-gray-100 text-gray-500" };
var STATUS_LABELS = { aberta: "Aberta", respondida: "Respondida", convertida: "Convertida", cancelada: "Cancelada" };
var TYPE_LABELS = { auto: "Auto", residencial: "Residencial", vida: "Vida", empresarial: "Empresarial" };

function QuoteRow({ quote, onEdit, onDelete, onConvert, isAdmin }) {
  var statusCls = STATUS_CLS[quote.status] || "bg-gray-100 text-gray-500";
  var statusLbl = STATUS_LABELS[quote.status] || quote.status;
  var typeLbl = TYPE_LABELS[quote.insurance_type] || quote.insurance_type;
  var propCount = quote.proposals ? quote.proposals.length : 0;

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50">
      <td className="py-3 px-4 text-sm text-gray-700">{quote.client_name}</td>
      <td className="py-3 px-4"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{typeLbl}</span></td>
      <td className="py-3 px-4 text-sm text-gray-600 max-w-[200px] truncate">{quote.description}</td>
      <td className="py-3 px-4 text-sm text-center font-medium text-[#0A2F3D]">{propCount}</td>
      <td className="py-3 px-4"><span className={"text-xs font-medium px-2 py-0.5 rounded-full " + statusCls}>{statusLbl}</span></td>
      <td className="py-3 px-4">
        <div className="flex gap-1">
          <button onClick={function() { onEdit(quote); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-[#3B82F6]" data-testid={"edit-quote-" + quote.id}><Edit2 className="w-4 h-4" /></button>
          {isAdmin && quote.status !== "convertida" && quote.selected_index != null && (
            <button onClick={function() { onConvert(quote.id); }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-500" title="Converter em apolice" data-testid={"convert-quote-" + quote.id}><ArrowRight className="w-4 h-4" /></button>
          )}
          {isAdmin && (
            <button onClick={function() { onDelete(quote.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={"delete-quote-" + quote.id}><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function QuotesTab() {
  var auth = useAuth();
  var isAdmin = auth.user && auth.user.role === "admin";
  var quotesState = useState([]);
  var quotes = quotesState[0];
  var setQuotes = quotesState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var showFormState = useState(false);
  var showForm = showFormState[0];
  var setShowForm = showFormState[1];
  var editingState = useState(null);
  var editing = editingState[0];
  var setEditing = editingState[1];
  var searchState = useState("");
  var search = searchState[0];
  var setSearch = searchState[1];

  var fetchQuotes = useCallback(function() {
    setLoading(true);
    var qs = "?limit=100";
    if (search) qs += "&search=" + encodeURIComponent(search);
    api.get("/quotes" + qs).then(function(res) { setQuotes(res.data.quotes || []); }).finally(function() { setLoading(false); });
  }, [search]);

  useEffect(function() { fetchQuotes(); }, [fetchQuotes]);

  function handleSave() { setShowForm(false); setEditing(null); fetchQuotes(); }
  function handleEdit(q) { setEditing(q); setShowForm(true); }
  function handleDelete(id) {
    if (!window.confirm("Excluir esta cotacao?")) return;
    api.delete("/quotes/" + id).then(function() { fetchQuotes(); });
  }
  function handleConvert(id) {
    if (!window.confirm("Converter esta cotacao em apolice?")) return;
    api.post("/quotes/" + id + "/convert").then(function() { fetchQuotes(); });
  }

  if (showForm) {
    return <QuoteForm quote={editing} onSave={handleSave} onCancel={function() { setShowForm(false); setEditing(null); }} />;
  }

  return (
    <div data-testid="quotes-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="Buscar cotacao..." className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56" data-testid="search-quotes" />
        </div>
        <button onClick={function() { setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg text-sm font-medium hover:bg-blue-600" data-testid="new-quote-btn">
          <Plus className="w-4 h-4" />Nova Cotacao
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Nenhuma cotacao cadastrada</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Descricao</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Propostas</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map(function(q) {
                return <QuoteRow key={q.id} quote={q} onEdit={handleEdit} onDelete={handleDelete} onConvert={handleConvert} isAdmin={isAdmin} />;
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
